import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";

import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Scatter,
  ResponsiveContainer,
} from "recharts";

import LoadingSpinner from "components/LoadingSpinner";
import useBPChart from "components/common/chart/bp_chart/useBPChart";
import BPChartTooltip from "components/common/chart/bp_chart/tooltip/BPChartTooltip";

const useStyles = makeStyles((theme) => ({
  root: {
    color: "#212121",
    border: "1px solid #E0E0E0",
    padding: "1rem",
  },
  tooltipVisible: {
    "& .recharts-tooltip-wrapper": {
      visibility: "visible !important",
    },
  },
}));

const BPChart = ({ isLoading, chartTitle, data, filter }) => {
  const classes = useStyles();
  const theme = useTheme();

  const {
    generateDataObject,
    CustomizedDot,

    lastDiastolic,
    lastSystolic,

    lastDiastolicX,
    lastDiastolicY,
    lastSystolicX,
    lastSystolicY,
  } = useBPChart(data);

  if (isLoading)
    return (
      <Grid
        className="trans-table__loading"
        style={{
          backgroundColor: theme.palette.background.paper,
          height: "150px",
        }}
      >
        <LoadingSpinner />
      </Grid>
    );

  const chartData = data && generateDataObject(data);

  return (
    <div style={{ position: "relative", marginTop: "5px" }}>
      <Box className={classes.root}>
        {chartTitle ? (
          <Grid item>
            <Typography variant="h4" style={{ fontSize: "18px" }}>
              {chartTitle}
            </Typography>
          </Grid>
        ) : (
          <></>
        )}
        <ResponsiveContainer width="100%" height="100%" aspect={4}>
          <ComposedChart
            width={500}
            height={400}
            data={chartData}
            margin={{
              top: 20,
              right: 80,
              bottom: 20,
              left: 20,
            }}
          >
            <CartesianGrid stroke="#eee" vertical={false} />
            <Tooltip
              content={data && <BPChartTooltip data={data} />}
              filterNull={false}
            />

            <Legend
              wrapperStyle={{
                bottom: "0 !important",
              }}
            />

            <XAxis
              dataKey="value"
              type="category"
              allowDuplicatedCategory={false}
              label={{
                value: "",
                position: "insideBottomLeft",
                offset: 0,
                fill: "#212121",
              }}
              tick={{
                fill: "black",
                fontSize: "10px",
              }}
              interval={filter === "last_year" ? 12 : 0}
              dy={20}
              offset={0}
              angle={-45}
            />
            <YAxis
              domain={[0, 225]}
              fill="fff"
              type="number"
              label={{
                value: "mmHg",
                angle: -90,
                position: "insideLeft",
                fill: "#212121",
                extraData: {
                  data: { hello: "world" },
                },
              }}
              tick={{ fill: "black" }}
            />
            <Scatter name="Systolic" dataKey="Systolic" fill="#0096bd" />
            <Scatter name="Diastolic" dataKey="Diastolic" fill="#f09000" />
            <Line
              dataKey="SystolicLine"
              stroke="#0096bd"
              dot={<CustomizedDot />}
              activeDot={false}
              legendType="none"
              strokeWidth={3}
            />
            <Line
              dataKey="DiastolicLine"
              stroke="#f09000"
              dot={<CustomizedDot />}
              activeDot={false}
              legendType="none"
              strokeWidth={3}
            />
          </ComposedChart>
        </ResponsiveContainer>

        <div
          style={{
            position: "absolute",
            top: lastSystolicY,
            left: lastSystolicX,
            color: "#212121",
            fontWeight: "bold",
            fontSize: 26,
          }}
        >
          {Number(data?.systolic?.mean?.value) > 0 ? lastSystolic : ""}
        </div>

        <div
          style={{
            position: "absolute",
            top: lastDiastolicY,
            left: lastDiastolicX,
            color: "#212121",
            fontWeight: "bold",
            fontSize: 26,
          }}
        >
          {Number(data?.diastolic?.mean?.value) > 0 ? lastDiastolic : ""}
        </div>

        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="flex-end"
          style={{ marginTop: "15px" }}
        >
          <Box m="1em">
            <Typography>
              <div style={{ fontWeight: "bold", fontSize: 20 }}>
                Mean:{" "}
                {`${data.systolic.mean.value}/${data.diastolic.mean.value}`}
              </div>
            </Typography>
          </Box>
          <Box m="1em">
            <Typography>
              <div style={{ fontSize: 20 }}>
                <strong>Min:</strong> <strong>{data.systolic.min.value}</strong>
                /{data.systolic.min.corrDia} {data.diastolic.min.corrSys}/
                <strong>{data.diastolic.min.value}</strong>
              </div>
            </Typography>
          </Box>
          <Box m="1em">
            <Typography>
              <div style={{ fontSize: 20 }}>
                <strong>Max:</strong> <strong>{data.systolic.max.value}</strong>
                /{data.systolic.max.corrDia} {data.diastolic.max.corrSys}/
                <strong>{data.diastolic.max.value}</strong>
              </div>
            </Typography>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default BPChart;
